import { operationIds } from "@gen/app-access-config";
import {
  CoreQuery,
  EmailFraudulentFlagBody,
  type IAdminTransaction,
} from "@gen/app-query";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";

import { HeadingContainer } from "~/components/heading_container";
import { useAuthOperations } from "~/contexts/auth_query";
import { useConfig } from "~/contexts/config";
import { isAdminTransaction } from "~/entities/transaction";
import { getAccountURL, getCustomerURL } from "~/services/stripe";

interface UserDetailsProps {
  transactionData: IAdminTransaction;
  isF2f?: boolean;
  userTransactionRole: "buyer" | "seller";
  stripeCustomerId?: string;
}

// NOTE We override stripe customer ID from user details,
// because of many edge cases where it's hard to destinguish
// the correct customer ID.
export function UserDetails({
  transactionData,
  userTransactionRole,
  stripeCustomerId,
}: UserDetailsProps) {
  const {
    stripe: stripe,
    auth: { host: keycloakHost, realm: keycloakRealm },
  } = useConfig();
  const { canExecuteAdminOperation } = useAuthOperations();
  const canToggleFraudulent = canExecuteAdminOperation(
    operationIds["core_admin.toggleFraudulentEmailFlag"],
  );

  const email =
    userTransactionRole === "buyer"
      ? transactionData?.buyer_email
      : transactionData?.seller_email;

  const guestEmail =
    userTransactionRole === "buyer"
      ? transactionData?.buyer_guest_email
      : transactionData?.seller_guest_email;

  const isFraudulentInitial =
    userTransactionRole === "buyer"
      ? transactionData?.buyer_fraudulent
      : transactionData?.seller_fraudulent;

  const stripeAccountId =
    userTransactionRole === "buyer"
      ? transactionData?.buyer_stripe_account_id
      : transactionData?.seller_stripe_account_id;

  let keycloakUserUrl: string | null = null;
  const userId =
    userTransactionRole === "buyer"
      ? transactionData?.buyer_id
      : transactionData?.seller_id;
  if (keycloakHost && keycloakRealm && userId) {
    keycloakUserUrl = `${keycloakHost}/auth/admin/master/console/#/${keycloakRealm}/users/${userId}/settings`;
  }

  const stripePlatformId = isAdminTransaction(transactionData)
    ? transactionData.stripe_platform_id
    : undefined;

  const [isFraudulentStatus, setFraudulentStatus] =
    useState<boolean>(!!isFraudulentInitial);

  useEffect(() => {
    setFraudulentStatus(!!isFraudulentInitial);
  }, [isFraudulentInitial]);

  const emailToMark = email || guestEmail;

  const fraudulentMutation =
    CoreQuery.useAdmin_toggleFraudulentEmailFlagMutation({
      meta: {
        success: `Email ${isFraudulentStatus ? "marked as" : "removed from"} high-risk`,
      },
    });

  const handleToggleFraudulent = () => {
    if (!emailToMark) {
      return;
    }

    const isNewStatus = !isFraudulentStatus;
    setFraudulentStatus(isNewStatus);

    fraudulentMutation.mutate(
      EmailFraudulentFlagBody.fromJS({
        email: emailToMark,
        fraudulent_flag: isNewStatus,
      }),
    );
  };

  return (
    <Box sx={{ mb: 5 }}>
      <HeadingContainer title="Contact" variant="h6" borderType="dashed">
        <Stack direction="row" spacing={3} alignItems="center">
          {keycloakUserUrl && (
            <Link
              href={keycloakUserUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${userTransactionRole}-keycloak-link`}
            >
              Keycloak User
            </Link>
          )}
          {userTransactionRole === "buyer" && stripeCustomerId && (
            <Link
              href={getCustomerURL(stripe, stripePlatformId, stripeCustomerId)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${userTransactionRole}-stripe-customer-link`}
            >
              Stripe Customer
            </Link>
          )}
          {userTransactionRole === "seller" && stripeAccountId && (
            <Link
              href={getAccountURL(stripe, stripePlatformId, stripeAccountId)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${userTransactionRole}-stripe-connect-link`}
            >
              Stripe Connect
            </Link>
          )}
        </Stack>
      </HeadingContainer>
      <Stack spacing={1}>
        <Stack spacing={0.5}>
          {guestEmail && (
            <Stack flexDirection="row" alignItems="center">
              <Typography variant="body2">Guest:&nbsp;</Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                data-testid={`${userTransactionRole}-guest-email-text`}
              >
                {guestEmail}
              </Typography>
            </Stack>
          )}
          {email && (
            <Stack flexDirection="row" justifyContent="space-between">
              <Stack flexDirection="row" alignItems="center">
                <Typography variant="body2">Full:&nbsp;</Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  data-testid={`${userTransactionRole}-email-text`}
                >
                  {email}
                </Typography>
              </Stack>
              <FormControlLabel
                sx={{
                  margin: 0,
                }}
                control={
                  <Switch
                    checked={isFraudulentStatus}
                    onChange={
                      canToggleFraudulent ? handleToggleFraudulent : undefined
                    }
                    disabled={
                      !canToggleFraudulent ||
                      !emailToMark ||
                      fraudulentMutation.isPending
                    }
                    size="small"
                    slotProps={{
                      input: {
                        "data-testid": `${userTransactionRole}-fraudulent-switch`,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2">Mark as high-risk</Typography>
                }
              />
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
